import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Pencil, Megaphone, Building2 } from "lucide-react";

/**
 * Retainer & campaign log — the home for ACCOUNT-LEVEL work that reviews score
 * but that has no production-asset equivalent: monthly retainer performance
 * (AM / GAD) and influencer/SMM campaigns (IMM). Videos & statics are NOT here
 * — makers' work is read straight from Production (the importer / asset board),
 * never re-typed. Each item auto-computes its objective band from the KPIs
 * entered here; subjective rating happens on the person's scorecard.
 */

const KINDS = [
  { key: "retainer_month", label: "Retainer months", icon: Building2,
    people: [["am", "Account manager"], ["gad", "Group account director"]],
    kpis: [["retainer_value_lakhs", "Retainer ₹L/mo", ""], ["sow_planned", "SOW planned (assets)", ""], ["sow_delivered", "SOW delivered", ""], ["client_health", "Client health 1-5", ""]] },
  { key: "campaign", label: "Campaigns", icon: Megaphone,
    people: [["owner", "Campaign owner (IMM/SMM)"]],
    kpis: [["budget_lakhs", "Budget ₹L", ""], ["revenue_lakhs", "Revenue ₹L", ""], ["margin_lakhs", "Margin ₹L", "optional"]] },
];

const BAND_COLORS = { HERO: "#16a34a", STRONG: "#2563eb", AVERAGE: "#d97706", RETHINK: "#dc2626" };
const bandFor = (s) => s == null ? null : s >= 4.5 ? "HERO" : s >= 3.5 ? "STRONG" : s >= 2.5 ? "AVERAGE" : "RETHINK";

function monthsSinceJan() {
  const out = [];
  const now = new Date();
  for (let y = 2026; y <= now.getFullYear(); y++) {
    const lastM = y === now.getFullYear() ? now.getMonth() : 11;
    for (let m = 0; m <= lastM; m++) {
      out.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    }
  }
  return out.reverse();
}

const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

export default function ReviewWorkLog() {
  const navigate = useNavigate();
  const months = useMemo(monthsSinceJan, []);
  const [month, setMonth] = useState(months[0]);
  const [kind, setKind] = useState("retainer_month");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} (new) | row
  const [busy, setBusy] = useState(false);

  const kindCfg = KINDS.find((k) => k.key === kind);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/reviews/work-items?month=${month}`);
      const all = res.data || [];
      const c = {};
      all.forEach((r) => { c[r.item_kind] = (c[r.item_kind] || 0) + 1; });
      setCounts(c);
      setRows(all.filter((r) => r.item_kind === kind));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load work items");
    }
  }, [month, kind]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((res) => setUsers(res.data || [])).catch(() => {});
  }, []);

  const startNew = () => setEditing({ item_kind: kind, title: "", month, link: "", account: "", model: "", people: {}, kpis: {} });

  const save = async () => {
    const e = editing;
    if (!e.title?.trim()) return toast.error(kind === "retainer_month" ? "Account name is required" : "Title is required");
    setBusy(true);
    try {
      if (e.work_id) {
        await api.put(`/reviews/work-items/${e.work_id}`, {
          title: e.title, month: e.month, link: e.link, account: e.account,
          model: e.model, people: e.people, kpis: e.kpis, notes: e.notes,
        });
      } else {
        await api.post("/reviews/work-items", e);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await api.delete(`/reviews/work-items/${row.work_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t1)", fontSize: 13 };
  const labelStyle = { fontSize: 11, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".04em", display: "block", marginBottom: 4 };

  return (
    <div className="dm-page-container" style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--t3)", fontSize: 13, marginBottom: 16, background: "none", border: "none", cursor: "pointer" }}>
        <ArrowLeft size={16} /> BACK
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>Retainer &amp; campaign log</h1>
        <button onClick={startNew} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
          <Plus size={16} /> Add {kindCfg.label.toLowerCase().replace(/s$/, "")}
        </button>
      </div>
      <p style={{ color: "var(--t3)", fontSize: 13, marginBottom: 18 }}>
        Account-level work that reviews score — retainer months (AM/GAD) and campaigns (IMM). Videos &amp; statics live in Production, not here. Backfill from January, then log month-on-month; KPIs here set each item's objective band, and subjective rating happens on the person's scorecard.
      </p>

      {/* Month strip */}
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, marginBottom: 12 }}>
        {months.map((m) => (
          <button key={m} onClick={() => setMonth(m)} style={{
            padding: "6px 12px", borderRadius: 999, whiteSpace: "nowrap", cursor: "pointer", fontSize: 12.5,
            border: `1px solid ${m === month ? "#1a56db" : "var(--bdr)"}`,
            background: m === month ? "rgba(26,86,219,.15)" : "var(--s1)",
            color: m === month ? "#7ca6f7" : "var(--t2)", fontWeight: m === month ? 700 : 400,
          }}>{monthLabel(m)}</button>
        ))}
      </div>

      {/* Kind tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = k.key === kind;
          return (
            <button key={k.key} onClick={() => setKind(k.key)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13,
              border: `1px solid ${active ? "#1a56db" : "var(--bdr)"}`,
              background: active ? "rgba(26,86,219,.15)" : "var(--s1)",
              color: active ? "#7ca6f7" : "var(--t2)", fontWeight: active ? 700 : 400,
            }}>
              <Icon size={15} /> {k.label}
              <span style={{ fontSize: 11, color: "var(--t3)" }}>{counts[k.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Items */}
      {rows.length === 0 ? (
        <div style={{ border: "1px dashed var(--bdr)", borderRadius: 12, padding: "48px 20px", textAlign: "center", color: "var(--t3)" }}>
          No {kindCfg.label.toLowerCase()} logged for {monthLabel(month)} yet. Hit "Add" to backfill.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((r) => {
            const band = bandFor(r.objective_score);
            return (
              <div key={r.work_id} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {r.link ? <a href={r.link} target="_blank" rel="noreferrer" style={{ color: "var(--t1)" }}>{r.title} ↗</a> : r.title}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 2 }}>
                    {[r.account, r.model, Object.entries(r.people_names || {}).map(([role, nm]) => `${role}: ${nm}`).join(" · ")].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "var(--t3)" }}>
                  {Object.entries(r.kpis || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ") || "no KPIs"}
                </div>
                {band && <span style={{ fontSize: 12, fontWeight: 700, color: BAND_COLORS[band] }}>{r.objective_score?.toFixed(1)} {band}</span>}
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setEditing({ ...r, people: r.people || {}, kpis: r.kpis || {} })} aria-label="Edit" style={{ padding: 7, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 7, color: "var(--t2)", cursor: "pointer" }}><Pencil size={14} /></button>
                  <button onClick={() => del(r)} aria-label="Delete" style={{ padding: 7, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 7, color: "#f87171", cursor: "pointer" }}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / edit sheet */}
      {editing && (
        <div onClick={() => setEditing(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "6vh", zIndex: 90 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(560px, 94vw)", maxHeight: "86vh", overflowY: "auto", background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14, padding: 22 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>
              {editing.work_id ? "Edit" : "Add"} {kindCfg.label.toLowerCase().replace(/s$/, "")} · {monthLabel(editing.month || month)}
            </h2>

            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label style={labelStyle}>{kind === "retainer_month" ? "Account name" : "Title"}</label>
                <input style={inputStyle} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} autoFocus />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Month</label>
                  <select style={inputStyle} value={editing.month} onChange={(e) => setEditing({ ...editing, month: e.target.value })}>
                    {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                  </select>
                </div>
                {kind !== "retainer_month" && (
                  <div>
                    <label style={labelStyle}>Account / client</label>
                    <input style={inputStyle} value={editing.account || ""} onChange={(e) => setEditing({ ...editing, account: e.target.value })} />
                  </div>
                )}
              </div>

              {(kind === "video" || kind === "static") && (
                <div>
                  <label style={labelStyle}>Link</label>
                  <input style={inputStyle} placeholder="https://…" value={editing.link || ""} onChange={(e) => setEditing({ ...editing, link: e.target.value })} />
                </div>
              )}

              <div style={{ borderTop: "1px solid var(--bdr)", paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>People</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {kindCfg.people.map(([role, label]) => (
                    <div key={role}>
                      <label style={labelStyle}>{label}</label>
                      <select style={inputStyle} value={(editing.people || {})[role] || ""}
                        onChange={(e) => setEditing({ ...editing, people: { ...editing.people, [role]: e.target.value } })}>
                        <option value="">—</option>
                        {users.map((u) => {
                          const uid = u.user_id || u.id;
                          return <option key={uid} value={uid}>{u.full_name || u.name || u.email}</option>;
                        })}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: "1px solid var(--bdr)", paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>KPIs (drive the objective score)</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {kindCfg.kpis.map(([key, label, ph]) => (
                    <div key={key}>
                      <label style={labelStyle}>{label}</label>
                      <input style={inputStyle} type="number" step="any" placeholder={ph} value={(editing.kpis || {})[key] ?? ""}
                        onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, [key]: e.target.value } })} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, minHeight: 56 }} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <button disabled={busy} onClick={save} style={{ flex: 1, padding: "11px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
                {editing.work_id ? "Save changes" : "Add to log"}
              </button>
              <button onClick={() => setEditing(null)} style={{ padding: "11px 18px", background: "var(--s2)", color: "var(--t2)", border: "1px solid var(--bdr)", borderRadius: 8, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
